import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { AcaoTagsVendaEnum } from '@shared/enums/vendas/acao-tags-venda.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { TagVenda } from '@shared/services/models/vendas/tag-venda.model';
import { AlterarTagsVendaRequest } from '@shared/services/requests/vendas/alterar-tags-venda.request';
import { rotuloDocumento } from '../../processo-venda.models';

export interface PvAlterarTagsDialogData {
    idsVenda: number[];
    tags: TagVenda[];
}

/**
 * "Alterar tags" do "Ações (n)" da Lista (T15 da US 143843, §10.3): incluir, remover ou substituir as tags dos
 * documentos selecionados. Mesmo diálogo das outras listagens (contas a receber, produtos), com a terceira
 * ação da story. O servidor responde documento a documento: os recusados (fora do escopo) aparecem no aviso.
 */
@Component({
    selector: 'app-pv-alterar-tags-dialog',
    standalone: true,
    imports: [
        FormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatInputModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './pv-alterar-tags-dialog.component.html',
    styleUrls: ['./pv-alterar-tags-dialog.component.css'],
})
export class PvAlterarTagsDialogComponent implements OnInit {
    public readonly acaoEnum = AcaoTagsVendaEnum;
    public acao: AcaoTagsVendaEnum = AcaoTagsVendaEnum.Incluir;
    public searchText = '';
    public tagsSelecionadas = new Set<number>();
    public tagsFiltradas: TagVenda[] = [];
    public isLoading = false;

    constructor(
        public dialogRef: MatDialogRef<PvAlterarTagsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PvAlterarTagsDialogData,
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
    ) {}

    ngOnInit(): void {
        this.tagsFiltradas = [...this.data.tags];
    }

    public filtrarTags(): void {
        const termo = this.searchText.toLowerCase().trim();
        this.tagsFiltradas = termo ? this.data.tags.filter(t => t.descricao.toLowerCase().includes(termo)) : [...this.data.tags];
    }

    public toggleTag(id: number): void {
        if (this.tagsSelecionadas.has(id)) this.tagsSelecionadas.delete(id);
        else this.tagsSelecionadas.add(id);
    }

    public isTagSelecionada(id: number): boolean {
        return this.tagsSelecionadas.has(id);
    }

    public get contadorText(): string {
        const n = this.tagsSelecionadas.size;
        if (n === 0) return '';
        return n === 1 ? '1 tag selecionada' : `${n} tags selecionadas`;
    }

    public get helpText(): string {
        switch (this.acao) {
            case AcaoTagsVendaEnum.Remover:
                return 'As tags selecionadas serão removidas dos documentos, mantendo as demais.';
            case AcaoTagsVendaEnum.Substituir:
                return 'Os documentos ficarão só com as tags selecionadas; sem nenhuma selecionada, ficam sem tag.';
            default:
                return 'As tags selecionadas serão adicionadas aos documentos, mantendo as existentes.';
        }
    }

    /** Substituir aceita lista vazia (tira todas); incluir e remover exigem ao menos uma. */
    public get podeAplicar(): boolean {
        return !this.isLoading && (this.tagsSelecionadas.size > 0 || this.acao === AcaoTagsVendaEnum.Substituir);
    }

    public onAcaoChange(): void {
        this.tagsSelecionadas.clear();
    }

    public salvar(): void {
        if (!this.podeAplicar) return;

        this.isLoading = true;

        const request = new AlterarTagsVendaRequest();
        request.idsVenda = this.data.idsVenda;
        request.idsTagVenda = Array.from(this.tagsSelecionadas);
        request.acao = this.acao;

        this.processoVendaService.alterarTags(request).subscribe({
            next: resposta => {
                this.isLoading = false;
                if (resposta.isError) {
                    this.alertService.show('Aviso', resposta.errorDescription, AlertType.warning);
                    return;
                }

                const recusados = (resposta.resultados ?? []).filter(r => !r.isAlterado);
                if (recusados.length > 0) {
                    const motivos = recusados.map(r => `${rotuloDocumento(r.numero, r.idVendaTipoDocumento)}: ${r.motivo}`).join(' ');
                    this.alertService.show('Aviso', `${recusados.length} documento(s) não alterado(s). ${motivos}`, AlertType.warning);
                } else {
                    this.alertService.show('Sucesso', 'Tags alteradas com sucesso!', AlertType.success);
                }
                this.dialogRef.close({ success: true });
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso', 'Não foi possível alterar as tags agora. Tente novamente em alguns instantes.', AlertType.warning);
            },
        });
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
